import React, { useState } from "react";
import SectionHeading from "@/components/ui/SectionHeading";
import TeamCard from "@/components/ui/TeamCard";
import useScrollReveal from "@/hooks/useScrollReveal";
import { COLORS, FONTS, LAYOUT } from "@/constants/theme";

const TEAM = [
  {
    name: "Alejandro Moreno",
    title: "Director Ejecutivo",
    role: "Fundador",
    image: "/images/team/alejandro-moreno.jpg",
    initials: "AM",
  },
  {
    name: "Samuel Macías",
    title: "Director de Operaciones",
    role: "Co-fundador",
    image: "/images/team/samuel-macias.jpg",
    initials: "SM",
  },
  {
    name: "Arianna Altamirano",
    title: "Directora de Desarrollo",
    role: "Co-fundadora",
    image: "/images/team/arianna-altamirano.jpg",
    initials: "AA",
  },
  {
    name: "Katty Salas",
    title: "Partner Associate",
    role: "Business & Commerce",
    image: "/images/team/katty-salas.jpeg",
    initials: "KS",
  },
];

const MV_CARD_STYLE = {
  backgroundColor: COLORS.lightSection,
  border: `1px solid ${COLORS.lightBorder}`,
  borderRadius: "20px",
  padding: "2rem 2rem 1.75rem",
  flex: "1 1 280px",
  boxShadow: "0 4px 24px rgba(0,104,255,0.07)",
};

const MV_ICON_STYLE = {
  width: "44px",
  height: "44px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "1rem",
};

const TeamSection = () => {
  const [headingRef, headingVisible] = useScrollReveal(0.2);
  const [mvRef, mvVisible] = useScrollReveal(0.15);
  const [teamOpen, setTeamOpen] = useState(false);

  return (
    <section
      className="px-6 py-24 md:py-32"
      id="nosotros"
      style={{ backgroundColor: COLORS.lightSurface, scrollMarginTop: LAYOUT.scrollOffset }}
    >
      <div className="mx-auto" style={{ maxWidth: LAYOUT.maxWidth }}>

        {/* Encabezado */}
        <div className={`reveal ${headingVisible ? "visible" : ""}`} ref={headingRef}>
          <SectionHeading
            align="left"
            lineColor={COLORS.digitalBlue}
            subtitle="Un equipo que convierte la complejidad en claridad, para que cada decisión esté respaldada por evidencia rigurosa."
            subtitleColor={COLORS.lightMuted}
            title="Nosotros"
            titleColor={COLORS.lightText}
          />
        </div>

        {/* Misión y Visión */}
        <div
          className={`reveal-stagger ${mvVisible ? "visible" : ""} mt-10`}
          ref={mvRef}
          style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}
        >
          {/* Misión */}
          <div style={MV_CARD_STYLE}>
            <div style={{ ...MV_ICON_STYLE, backgroundColor: "rgba(0,104,255,0.10)" }}>
              <svg fill="none" height="22" viewBox="0 0 24 24" width="22">
                <path
                  d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                  stroke={COLORS.digitalBlue}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </div>
            <h3
              style={{
                color: COLORS.lightText,
                fontFamily: FONTS.heading,
                fontSize: "0.86rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                marginBottom: "0.75rem",
              }}
            >
              Misión
            </h3>
            <p style={{ color: COLORS.lightMuted, fontFamily: FONTS.body, fontSize: "1rem", lineHeight: 1.75 }}>
              Convertir datos en decisiones estratégicas para instituciones públicas y empresas privadas del Ecuador,
              aplicando análisis riguroso que reduce la incertidumbre y orienta la acción con evidencia.
            </p>
          </div>

          {/* Visión */}
          <div style={MV_CARD_STYLE}>
            <div style={{ ...MV_ICON_STYLE, backgroundColor: "rgba(251,139,30,0.10)" }}>
              <svg fill="none" height="22" viewBox="0 0 24 24" width="22">
                <circle cx="12" cy="12" r="3" stroke={COLORS.webOrange} strokeWidth="2" />
                <path
                  d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"
                  stroke={COLORS.webOrange}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </div>
            <h3
              style={{
                color: COLORS.lightText,
                fontFamily: FONTS.heading,
                fontSize: "0.86rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                marginBottom: "0.75rem",
              }}
            >
              Visión
            </h3>
            <p style={{ color: COLORS.lightMuted, fontFamily: FONTS.body, fontSize: "1rem", lineHeight: 1.75 }}>
              Consolidarnos como la principal firma de consultoría en datos del Ecuador, referente en el sector
              público y privado por la solidez de nuestro análisis y la relevancia de nuestros resultados.
            </p>
          </div>
        </div>

        {/* Separador */}
        <div
          style={{
            margin: "3rem 0 2rem",
            height: "1px",
            background: `linear-gradient(90deg, transparent, ${COLORS.lightBorder}, transparent)`,
          }}
        />

        {/* Acordeón Equipo */}
        <div>
          <button
            aria-controls="team-panel"
            aria-expanded={teamOpen}
            onClick={() => setTeamOpen((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              background: COLORS.digitalBlue,
              border: "none",
              borderRadius: "12px",
              padding: "0.85rem 1.6rem",
              cursor: "pointer",
              fontFamily: FONTS.heading,
              fontSize: "1rem",
              fontWeight: 600,
              color: "#ffffff",
              letterSpacing: "0.02em",
              boxShadow: "0 4px 18px rgba(0,104,255,0.28)",
              transition: "background 180ms ease, box-shadow 180ms ease",
            }}
            type="button"
          >
            <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
              <path
                d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75M9 7a4 4 0 110 8 4 4 0 010-8z"
                stroke="#ffffff"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.8"
              />
            </svg>
            Nuestro Equipo
            <span
              style={{
                marginLeft: "0.25rem",
                color: "#ffffff",
                fontSize: "1.1rem",
                fontWeight: 700,
                transition: "transform 250ms ease",
                display: "inline-block",
                transform: teamOpen ? "rotate(45deg)" : "rotate(0deg)",
              }}
            >
              +
            </span>
          </button>

          {/* Panel colapsable.
              Se anima con grid-template-rows en lugar de un max-height fijo: el tope
              de 900px recortaba la última tarjeta en móvil, donde el contenido mide
              ~1283px en una sola columna. Así la altura la calcula el navegador. */}
          <div
            id="team-panel"
            style={{
              display: "grid",
              gridTemplateRows: teamOpen ? "1fr" : "0fr",
              transition: "grid-template-rows 420ms cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            <div style={{ overflow: "hidden", minHeight: 0 }}>
              <div
                className={`reveal-stagger ${teamOpen ? "visible" : ""} mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4`}
              >
                {TEAM.map((member) => (
                  <TeamCard
                    image={member.image}
                    initials={member.initials}
                    key={member.name}
                    mode="light"
                    name={member.name}
                    role={member.role}
                    title={member.title}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default TeamSection;
